import React, { FC, useState } from 'react';
import { IReviewNew } from '../../../shared/interfaces/IReviewNew';
import { Box, Divider, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ReviewWrapper from '../ReviewWrapper';
import ReviewHeader from '../ReviewHeader';
import ReviewContent from '../ReviewContent';
import ReviewFooter from '../ReviewFooter';
import { LocationOn, Person } from '@mui/icons-material';
import Block from '../../Block/Block';
import { CommunityManagement } from '../../../shared/interfaces/ICommunityManagement';
import ReviewText from '../ReviewText';
import useAIReply from '../../../hooks/useAIReplay';

interface Props extends IReviewNew {
  communityManagement?: CommunityManagement;
  commentingModule: boolean;
  hideHeader: boolean;
  hideFooter: boolean;
}

const JamedaReview: FC<Props> = ({
  id,
  review,
  platform,
  reviewComment,
  commentingModule,
  communityManagement,
  note,
  deletion,
  deleted,
  timestamp,
  hideHeader,
  hideFooter,
  history,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [showAllBlocks, setShowAllBlocks] = useState(false);

  const {
    username,
    age,
    insurance,
    location,
    title,
    text,
    rating,
    medicalPractitioner,
    mandatoryAssessments,
    optionalAssessments,
    companyComment,
  } = review;

  const AIReply = useAIReply(
    title,
    rating,
    [],
    text,
    '',
    '',
    mandatoryAssessments,
    optionalAssessments
  );

  return (
    <ReviewWrapper>
      <ReviewHeader
        hide={hideHeader}
        rating={rating}
        platform={platform}
        reviewFor={medicalPractitioner}
        timestamp={timestamp}
        id={id}
        note={note}
        deletion={deletion}
        commentingModule={commentingModule}
        deleted={deleted}
      />
      <ReviewContent>
        {age && age !== 'nicht angegeben' && (
          <Box sx={{ display: 'flex', alignItems: 'center', color: theme.palette.text.secondary }}>
            <Person fontSize="small" />
            <Typography
              variant="caption"
              sx={{
                ml: theme.spacing(1),
              }}
            >
              {age} {insurance && insurance !== 'nicht angegeben' && `(${insurance})`}
            </Typography>
          </Box>
        )}
        {location && (
          <Box sx={{ display: 'flex', alignItems: 'center', color: theme.palette.text.secondary }}>
            <LocationOn fontSize="small" />
            <Typography
              variant="caption"
              sx={{
                ml: theme.spacing(1),
              }}
            >
              {location}
            </Typography>
          </Box>
        )}
        {title && (
          <Typography
            variant="h6"
            sx={{
              mt: theme.spacing(2),
            }}
          >
            {title}
          </Typography>
        )}
        <ReviewText text={text} />
        {mandatoryAssessments && mandatoryAssessments.length > 0 && (
          <Box sx={{ mt: theme.spacing(2) }}>
            {showAllBlocks
              ? mandatoryAssessments.map(({ rating, original_rating, title }, index) => (
                  <Box
                    key={`Review_${id}_assessment_${index}`}
                    sx={{ mt: theme.spacing(1) }}
                  >
                    <Divider orientation="horizontal" />
                    <Block
                      rating={rating}
                      original_rating={original_rating}
                      title={title}
                    />
                  </Box>
                ))
              : mandatoryAssessments.map(({ rating, original_rating, title }, index) => (
                  <Box
                    key={`Review_${id}_assessment_${index}`}
                    sx={{ mt: theme.spacing(1) }}
                  >
                    <Divider orientation="horizontal" />
                    <Block
                      rating={rating}
                      original_rating={original_rating}
                      title={title}
                    />
                  </Box>
                ))}
          </Box>
        )}
        {optionalAssessments && optionalAssessments.length > 0 && showAllBlocks && (
          <Box sx={{ mt: theme.spacing(2) }}>
            <Typography
              variant="h6"
              sx={{ mb: theme.spacing(1) }}
            >
              Optionale Noten
            </Typography>
            {optionalAssessments.map(({ rating, original_rating, title }, index) => {
              return (
                <Box
                  key={`Review_${id}_OptAssessment_${index}`}
                  sx={{ mt: theme.spacing(1) }}
                >
                  <Divider orientation="horizontal" />
                  <Block
                    rating={rating}
                    original_rating={original_rating}
                    title={title}
                  />
                </Box>
              );
            })}
          </Box>
        )}
      </ReviewContent>
      <ReviewFooter
        platform={platform}
        history={history}
        hide={hideFooter}
        reviewId={id}
        review={review}
        username={username}
        input={AIReply}
        reviewComment={reviewComment}
        communityManagement={communityManagement}
        commentingModule={commentingModule}
        companyComment={companyComment}
      />
    </ReviewWrapper>
  );
};

export default JamedaReview;
